/* eslint-disable @typescript-eslint/no-explicit-any */
/* eslint-disable @typescript-eslint/explicit-module-boundary-types */
/* eslint-disable @typescript-eslint/no-unsafe-member-access */

import { AnimationManager } from './AnimationManager';
import { AnimationPlayStyle } from './AnimationPlayStyle';
import { AnimationStyle } from './AnimationStyle';
import { PaneChannelTiming, PaneChannelTimings } from './AnimationTimingTypes';
import { DataStorePaneState } from './DataStorePaneState';
import { isMapVizType } from './DataStorePaneStateUtility';
import { DataStorePaths } from './DataStorePaths';
import { HuttParser } from './HuttParser';
import { IDataStore } from './IDataStore';
import { injectExitMarksIntoBase } from './MapExitMarkInjection';
import { PaneAnimationInfo } from './PaneAnimationInfo';
import { PaneStateAnimation } from './PaneStateAnimation';
import { PaneStateAttribute } from './PaneStateAttribute';
import { WarpingFunction } from './WarpingFunctionMapUtil';

/**
 * High-level controller for mark animations.
 *
 * This class provides the main API for viz-client to create and manage animations.
 * It uses a handle-based pattern where each controller instance is identified by
 * a numeric handle, matching the C++ API design.
 *
 * Usage pattern:
 * ```typescript
 * // Create a controller
 * const handle = MarkAnimationController.createController();
 *
 * // Start an animation
 * const success = MarkAnimationController.startAnimation(
 *   handle,
 *   fromDataStore,
 *   toDataStore,
 *   1000, // duration in ms
 *   AnimationPlayStyle.Linear
 * );
 *
 * // In render loop, check for frames
 * if (MarkAnimationController.hasAnimationFrame(handle)) {
 *   const frame = MarkAnimationController.getAnimationFrame(handle);
 *   // Render the frame
 * }
 *
 * // Clean up
 * MarkAnimationController.deleteController(handle);
 * ```
 *
 * Port of C++ MarkAnimationController pattern from tabanim.
 */
export class MarkAnimationController {
  private static handleCounter = 1;
  private static controllers = new Map<number, MarkAnimationController>();

  // Sheet/Group Key Management (mirrors C++ PlayedAnimationTracker)
  private static currentGroupKey?: string;
  private static groupKeys = new Set<string>();

  // Replay Speed Management (mirrors C++ AnimationManager::m_replaySpeed)
  // Default speed is 1.0 (actual speed). Matches C++ AnimationManager.cpp:131
  private static replaySpeed = 1.0;

  private currentInterpolatedFrame: IDataStore | null = null;
  private currentAnimation: PaneStateAnimation | null = null;
  private previousTargetDataStore: IDataStore | null = null;
  private previousDataStoreRaw: any = null; // Cache raw input to detect identical datastores
  private lastCompletedFrame: IDataStore | null = null; // Track the final displayed frame

  // Replay state - stores FROM/TO datastores from the last completed animation
  // Mirrors C++ MarkAnimationController replay tracking
  private replayFromDataStore: IDataStore | null = null;
  private replayToDataStore: IDataStore | null = null;
  private replayDurationMs = 0;
  private hasAnimationToReplay = false;

  // Sheet ID for this controller (for telemetry/debugging)
  private sheetId?: string;

  // Pane animation info map - tracks enter/move/exit marks per pane
  // Mirrors C++ m_paneAnimationInfoMap from MarkAnimationController
  private paneAnimationInfoMap: Map<string, PaneAnimationInfo> = new Map();

  // Current animation style (Synced vs Phased), set each time startAnimationWithTarget is called
  private m_animationStyle: AnimationStyle = AnimationStyle.Phased;

  /**
   * Create a new mark animation controller.
   *
   * @returns A handle (numeric ID) for this controller
   */
  public static createController(): number {
    const handle = this.handleCounter++;
    this.controllers.set(handle, new MarkAnimationController());
    return handle;
  }

  /**
   * Delete a controller and clean up its resources.
   *
   * @param handle - The controller handle
   */
  public static deleteController(handle: number): void {
    const controller = this.controllers.get(handle);
    if (controller) {
      controller.cleanup();
      this.controllers.delete(handle);
    }
  }

  /**
   * Start an animation between two datastores.
   *
   * NOTE: This method is only used for testing. Viz-client uses startAnimationWithTarget() instead,
   * which maintains state internally (previousTargetDataStore pattern). This method allows explicit
   * control over FROM and TO states, useful for testing and debugging.
   *
   * @param handle - The controller handle
   * @param fromDataStore - The starting datastore (can be HUTT JSON or IDataStore)
   * @param toDataStore - The ending datastore (can be HUTT JSON or IDataStore)
   * @param durationMs - Duration in milliseconds
   * @param style - Animation play style (Linear, EaseIn, EaseOut, etc.)
   * @returns true if animation started successfully, false otherwise
   */
  public static startAnimation(
    handle: number,
    fromDataStore: any,
    toDataStore: any,
    durationMs: number,
    style: AnimationPlayStyle
  ): boolean {
    const controller = this.controllers.get(handle);
    if (!controller) {
      return false;
    }

    try {
      return controller.startAnimationImpl(fromDataStore, toDataStore, durationMs, style);
    } catch (error) {
      console.error('[MarkAnimationController] Error starting animation:', error);
      return false;
    }
  }

  /**
   * Check if the controller has an animation frame ready.
   *
   * @param handle - The controller handle
   * @returns true if a frame is available
   */
  public static hasAnimationFrame(handle: number): boolean {
    const controller = this.controllers.get(handle);
    if (!controller) {
      return false;
    }
    return controller.currentInterpolatedFrame !== null;
  }

  /**
   * Get the current animation frame and consume it.
   *
   * This follows a pull-based pattern: the caller requests a frame, and
   * the controller returns it once, then clears it. Calling this method
   * twice without an intervening tick will return null the second time.
   *
   * @param handle - The controller handle
   * @returns The interpolated frame, or null if no frame is available
   */
  public static getAnimationFrame(handle: number): IDataStore | null {
    const controller = this.controllers.get(handle);
    if (!controller) {
      return null;
    }

    // Read and consume the frame
    const frame = controller.currentInterpolatedFrame;
    controller.currentInterpolatedFrame = null;

    return frame;
  }

  /**
   * Get the number of active controllers.
   * Useful for testing and debugging.
   */
  public static getControllerCount(): number {
    return this.controllers.size;
  }

  /**
   * Clear all controllers.
   * Useful for testing.
   */
  public static clearAllControllers(): void {
    this.controllers.forEach((controller) => controller.cleanup());
    this.controllers.clear();
    this.handleCounter = 1;
  }

  /**
   * Set sheet ID for a controller.
   * Used for telemetry and debugging purposes.
   *
   * Mirrors C++ SetSheetId() from MarkAnimationControllerInterface.cpp
   *
   * @param handle - The controller handle
   * @param sheetId - The sheet ID (string)
   */
  public static setSheetId(handle: number, sheetId: string): void {
    const controller = this.controllers.get(handle);
    if (!controller) {
      return;
    }
    controller.sheetId = sheetId;
  }

  /**
   * Get sheet ID for a controller.
   *
   * @param handle - The controller handle
   * @returns The sheet ID, or undefined if not set or invalid handle
   */
  public static getSheetId(handle: number): string | undefined {
    const controller = this.controllers.get(handle);
    return controller?.sheetId;
  }

  /**
   * Set the current group key.
   * Used for managing animation replay contexts per group (typically per sheet).
   *
   * Mirrors C++ PlayedAnimationTracker::SetCurrentGroupKey()
   *
   * @param key - The group key (typically sheet name/locator)
   */
  public static setCurrentGroupKey(key: string): void {
    this.currentGroupKey = key;
    this.groupKeys.add(key);
  }

  /**
   * Get the current group key.
   *
   * @returns The current group key, or undefined if not set
   */
  public static getCurrentGroupKey(): string | undefined {
    return this.currentGroupKey;
  }

  /**
   * Delete a group key.
   * If the deleted key is the current group key, clears the current group key.
   *
   * Mirrors C++ PlayedAnimationTracker::DeleteGroupKey()
   *
   * @param key - The group key to delete
   */
  public static deleteGroupKey(key: string): void {
    this.groupKeys.delete(key);
    // If deleting the current group key, clear it
    if (this.currentGroupKey === key) {
      this.currentGroupKey = undefined;
    }
  }

  /**
   * Delete all group keys.
   * Clears all group keys and the current group key.
   *
   * Mirrors C++ PlayedAnimationTracker::DeleteAllGroupKeys()
   */
  public static deleteAllGroupKeys(): void {
    this.groupKeys.clear();
    this.currentGroupKey = undefined;
  }

  /**
   * Set the global replay speed multiplier.
   * This speed is applied when replay animations are triggered.
   *
   * Mirrors C++ AnimationManager::SetReplaySpeed() (line 490-493)
   *
   * @param speed - Speed multiplier (0.5 = half speed, 1.0 = actual speed, 2.0 = double speed)
   */
  public static setReplaySpeed(speed: number): void {
    this.replaySpeed = speed;
  }

  /**
   * Get the current global replay speed multiplier.
   *
   * Mirrors C++ AnimationManager::GetReplaySpeed() (line 485-488)
   *
   * @returns The current replay speed multiplier
   */
  public static getReplaySpeed(): number {
    return this.replaySpeed;
  }

  /**
   * Check if any controller has frames ready.
   * Used by tick() to determine return value.
   */
  public static hasAnyFramesReady(): boolean {
    let hasFrames = false;
    this.controllers.forEach((controller) => {
      if (controller.currentInterpolatedFrame !== null) {
        hasFrames = true;
      }
    });
    return hasFrames;
  }

  /**
   * Start animation with a single target datastore.
   * Uses previousTargetDataStore as FROM state, and newTarget as TO state.
   * Matches C++ pattern where each new datastore becomes the animation target.
   *
   * @param newTargetDataStore - The new target datastore (TO state)
   * @param durationMs - Duration in milliseconds
   * @param style - Animation play style
   * @returns true if animation started, false otherwise
   */
  public static startAnimationWithTarget(
    handle: number,
    newTargetDataStore: any,
    durationMs: number,
    style: AnimationPlayStyle,
    animationStyle: AnimationStyle
  ): boolean {
    const controller = this.controllers.get(handle);
    if (!controller) {
      return false;
    }

    controller.m_animationStyle = animationStyle;

    try {
      return controller.startAnimationWithTargetImpl(newTargetDataStore, durationMs, style);
    } catch (error) {
      console.error('[MarkAnimationController] Error starting animation with target:', error);
      return false;
    }
  }

  /**
   * Set the previous target datastore explicitly.
   * Called by viz-client via _SetPreviousTargetDataStore to tell us what the current visual state is.
   *
   * @param handle - The controller handle
   * @param dataStore - The current datastore (hex string or object)
   */
  public static setPreviousTargetDataStore(handle: number, dataStore: any): void {
    const controller = this.controllers.get(handle);
    if (!controller) {
      return;
    }

    const parsedDS = controller.parseDataStore(dataStore, 'previousTargetDataStore');
    if (!parsedDS) {
      return;
    }

    // Set both the previous target and last completed frame
    controller.previousTargetDataStore = parsedDS.clone();
    controller.lastCompletedFrame = parsedDS.clone();
    // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
    controller.previousDataStoreRaw = dataStore;
  }

  /**
   * Reset the controller (clear previous target).
   * Mirrors C++ MarkAnimationController::Reset() line 143-149
   *
   * @param handle - The controller handle
   */
  public static resetController(handle: number): void {
    const controller = this.controllers.get(handle);
    if (controller) {
      controller.previousTargetDataStore = null;
      controller.lastCompletedFrame = null;
      controller.paneAnimationInfoMap.clear();
      controller.sheetId = undefined; // Clear sheet ID on reset
      controller.replayFromDataStore = null;
      controller.replayToDataStore = null;
      controller.replayDurationMs = 0;
      controller.hasAnimationToReplay = false;
    }
  }

  /**
   * Replay the last completed animation for a controller.
   * Re-creates the animation from the stored FROM→TO datastores.
   */
  public static replayAnimation(handle: number, replaySpeed?: number): boolean {
    const controller = this.controllers.get(handle);
    if (!controller) {
      return false;
    }

    if (!controller.hasAnimationToReplay || !controller.replayFromDataStore || !controller.replayToDataStore) {
      return false;
    }

    // Use provided speed, or fall back to global replay speed (matches C++ pattern)
    const speed = replaySpeed ?? this.replaySpeed;

    // Apply replay speed to duration (matches C++ UpdateRockerAnimation behavior:
    // curInfo->duration = curInfo->originalDuration * m_replaySpeed)
    const replayDuration = controller.replayDurationMs * speed;

    // Re-create the animation from stored FROM→TO states
    // Use Rocker style to match C++ behavior
    // Pass isReplay=true to prevent overwriting the original duration (which would cause speeds to compound)
    return controller.startAnimationImpl(
      controller.replayFromDataStore.clone(),
      controller.replayToDataStore.clone(),
      replayDuration,
      AnimationPlayStyle.Rocker,
      true // isReplay = true
    );
  }

  /**
   * Check if a controller has an animation available to replay.
   *
   * Mirrors C++ MarkAnimationController::GetHasAnimationToReplay()
   *
   * @param handle - The controller handle
   * @returns true if the controller has a completed animation that can be replayed
   */
  public static getHasAnimationToReplay(handle: number): boolean {
    const controller = this.controllers.get(handle);
    if (!controller) {
      return false;
    }
    return controller.hasAnimationToReplay;
  }

  /**
   * Get the pane animation info map for a controller.
   * Useful for testing and debugging mark scenario detection.
   *
   * @param handle - The controller handle
   * @returns Map of pane ID to PaneAnimationInfo, or null if controller not found
   */
  public static getPaneAnimationInfoMap(handle: number): Map<string, PaneAnimationInfo> | null {
    const controller = this.controllers.get(handle);
    if (!controller) {
      return null;
    }
    return controller.paneAnimationInfoMap;
  }

  /**
   * Internal implementation of startAnimationWithTarget.
   * Uses previousTargetDataStore as FROM, newTarget as TO.
   */
  private startAnimationWithTargetImpl(newTargetDataStore: any, durationMs: number, style: AnimationPlayStyle): boolean {
    // Check if raw input is identical to previous (like C++ pointer equality check)
    if (newTargetDataStore === this.previousDataStoreRaw) {
      return false;
    }

    // Parse the new target datastore
    const newTargetDS = this.parseDataStore(newTargetDataStore, 'newTargetDataStore');
    if (!newTargetDS) {
      console.error('[MarkAnimationController] Failed to parse new target datastore');
      return false;
    }

    // Determine FROM state: use last completed frame if available, otherwise use previous target
    let fromDS: IDataStore;
    if (!this.previousTargetDataStore) {
      // IMPORTANT: Clone the datastore to avoid sharing references
      this.previousTargetDataStore = newTargetDS.clone();
      this.lastCompletedFrame = newTargetDS.clone(); // Also save as last frame
      // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
      this.previousDataStoreRaw = newTargetDataStore;
      return false; // No animation on first call (no FROM state)
    }

    // Use last completed frame if available (most recent displayed state)
    if (this.lastCompletedFrame) {
      fromDS = this.lastCompletedFrame;
    } else {
      fromDS = this.previousTargetDataStore;
    }

    // Animate FROM current TO new
    const success = this.startAnimationImpl(fromDS, newTargetDS, durationMs, style);

    // Save new target as previous for next animation
    // IMPORTANT: Clone the datastore to avoid sharing references
    if (success) {
      this.previousTargetDataStore = newTargetDS.clone();
      // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
      this.previousDataStoreRaw = newTargetDataStore;
    } else {
      this.lastCompletedFrame = newTargetDS.clone();
    }

    return success;
  }

  /**
   * Internal implementation of startAnimation.
   * Mirrors C++ MarkAnimationController::AttemptAnimation() line 460-591
   * @param isReplay - If true, don't update replay state (prevents compounding speed changes)
   */
  private startAnimationImpl(
    fromDataStore: any,
    toDataStore: any,
    durationMs: number,
    style: AnimationPlayStyle,
    isReplay = false
  ): boolean {
    // Check if raw inputs are identical BEFORE parsing (like C++ pointer equality check)
    // This prevents unnecessary animation when server sends the same datastore twice
    if (fromDataStore === toDataStore) {
      return false;
    }

    // Cancel any existing animation
    if (this.currentAnimation) {
      this.cleanup();
    }

    // Clear pane animation info map (mirrors C++ line 468)
    this.paneAnimationInfoMap.clear();

    const parsed = this.parseDataStores(fromDataStore, toDataStore);
    if (!parsed) {
      return false;
    }
    const { fromDS, toDS } = parsed;

    if (!this.shouldAnimate(fromDS, toDS)) {
      return false;
    }

    // Build pane animation info map (mirrors C++ BuildPaneAnimationInfoMap line 515)
    // This analyzes marks to determine enter/move/exit for each pane
    this.paneAnimationInfoMap = PaneAnimationInfo.buildForAllPanes(fromDS, toDS, this.m_animationStyle);
    const playbackDurationMs = isReplay ? durationMs : this.computePlaybackDurationMs(durationMs);
    if (playbackDurationMs <= 0) {
      return false;
    }

    const nonMarksTiming = this.aggregateNonMarksTiming();
    const paneChannelTimingsMap = this.buildPaneChannelTimingsMap();
    const overallWarpingFunction = this.aggregateOverallWarpingFunction();
    this.currentAnimation = this.createPaneAnimation(fromDS, toDS, nonMarksTiming, paneChannelTimingsMap, overallWarpingFunction);

    // Play the animation via AnimationManager
    // For Rocker (replay), playAnimation now starts at fraction=1.0 backward (matching C++)
    const animationManager = AnimationManager.getInstance();
    animationManager.playAnimation(this.currentAnimation, playbackDurationMs, undefined, style);

    // Save replay state so this animation can be replayed later
    // Only save if this is NOT a replay (prevents compounding speed changes)
    // Mirrors C++ behavior where completed animations are stored for replay
    if (!isReplay) {
      this.replayFromDataStore = fromDS.clone();
      this.replayToDataStore = toDS.clone();
      this.replayDurationMs = playbackDurationMs;
      this.hasAnimationToReplay = true;
    }

    return true;
  }

  /**
   * Parse raw datastore inputs into IDataStore instances.
   * Returns parsed FROM/TO datastores, or null if parsing fails.
   */
  private parseDataStores(fromDataStore: any, toDataStore: any): { fromDS: IDataStore; toDS: IDataStore } | null {
    const fromDS = this.parseDataStore(fromDataStore, 'fromDataStore');
    const toDS = this.parseDataStore(toDataStore, 'toDataStore');

    if (!fromDS || !toDS) {
      return null;
    }

    return { fromDS, toDS };
  }

  /**
   * Check whether parsed datastores contain animatable content.
   * Returns false if TO has no PDMarks or there are no common panes.
   */
  private shouldAnimate(fromDS: IDataStore, toDS: IDataStore): boolean {
    // Validate target datastore has PDMarks (mirrors C++ line 470-473)
    if (!DataStorePaths.hasPDMarks(toDS)) {
      return false;
    }

    // Check if there are common panes between FROM and TO (mirrors C++ line 501-506)
    // If pane descriptor IDs changed (no overlap), there's nothing to interpolate between.
    const panesToAnimate = DataStorePaths.getPanesToAnimate(fromDS, toDS);
    return panesToAnimate.length > 0;
  }

  /**
   * Build per-pane channel timing map for per-mark enter/move/exit interpolation.
   * C++ Reference: PaneAnimationInfo::GetAnimationParams() per AnimationType.
   */
  private buildPaneChannelTimingsMap(): Map<string, PaneChannelTimings> {
    const result = new Map<string, PaneChannelTimings>();
    this.paneAnimationInfoMap.forEach((paneInfo, paneId) => {
      const channelTimings = paneInfo.buildPaneChannelTimings();
      if (channelTimings) {
        result.set(paneId, channelTimings);
      }
    });
    return result;
  }

  private buildAnimatingPaneIds(): Set<string> {
    const result = new Set<string>();
    this.paneAnimationInfoMap.forEach((paneInfo, paneId) => {
      if (paneInfo.hasMarksToAnimate()) {
        result.add(paneId);
      }
    });
    return result;
  }

  /**
   * Aggregate nonMarks timing across all panes in the map.
   * Returns the first pane's timing — all panes share the same AnimationStyle
   * so timings will be uniform for a given style/scenario combination.
   */
  private aggregateNonMarksTiming(): PaneChannelTiming | null {
    let result: PaneChannelTiming | null = null;
    this.paneAnimationInfoMap.forEach((paneInfo) => {
      if (result === null) {
        result = paneInfo.getShadowNonMarksTiming();
      }
    });
    return result;
  }

  private aggregateNonMarkAttributeTimingOverrides(): Map<PaneStateAttribute, PaneChannelTiming> | null {
    let result: Map<PaneStateAttribute, PaneChannelTiming> | null = null;
    this.paneAnimationInfoMap.forEach((paneInfo) => {
      if (result === null) {
        result = paneInfo.getShadowNonMarkAttributeTimingOverrides();
      }
    });
    return result;
  }

  private aggregateOverallWarpingFunction(): WarpingFunction | null {
    let result: WarpingFunction | null = null;
    this.paneAnimationInfoMap.forEach((paneInfo) => {
      if (result === null) {
        result = paneInfo.getShadowOverallWarpingFunction();
      }
    });
    return result;
  }

  private computePlaybackDurationMs(requestedDurationMs: number): number {
    let maxRawDuration = 0;
    this.paneAnimationInfoMap.forEach((paneInfo) => {
      const rawDuration = paneInfo.getShadowTotalDuration();
      if (rawDuration > maxRawDuration) {
        maxRawDuration = rawDuration;
      }
    });
    return maxRawDuration > 0 ? requestedDurationMs * maxRawDuration : 0;
  }

  private createPaneAnimation(
    fromDS: IDataStore,
    toDS: IDataStore,
    nonMarksTiming: PaneChannelTiming | null,
    paneChannelTimingsMap: Map<string, PaneChannelTimings>,
    overallWarpingFunction: WarpingFunction | null
  ): PaneStateAnimation {
    const fromState = new DataStorePaneState(fromDS);
    const toState = this.createToState(fromDS, toDS);

    return new PaneStateAnimation(
      fromState,
      toState,
      (frame: IDataStore) => {
        this.currentInterpolatedFrame = frame;
        this.lastCompletedFrame = frame.clone();
      },
      toDS,
      nonMarksTiming,
      this.aggregateNonMarkAttributeTimingOverrides(),
      paneChannelTimingsMap,
      this.buildAnimatingPaneIds(),
      overallWarpingFunction
    );
  }

  private createToState(fromDS: IDataStore, toDS: IDataStore): DataStorePaneState {
    if (isMapVizType(fromDS)) {
      return this.createToStateForMap(fromDS, toDS);
    } else {
      // For non-map types, use original TO datastore directly
      return new DataStorePaneState(toDS);
    }
  }

  private createToStateForMap(fromDS: IDataStore, toDS: IDataStore): DataStorePaneState {
    // Clone TO datastore and inject FROM-only exit marks for map exit animations.
    // Keep the original TO datastore for the final frame.
    const toDSWithExitMarks = toDS.clone();
    const exitingToIndicesMap = injectExitMarksIntoBase(fromDS, toDSWithExitMarks);

    const toState = new DataStorePaneState(toDSWithExitMarks);
    toState.setExitingToIndicesMap(exitingToIndicesMap);
    return toState;
  }

  /**
   * Parse a datastore from various formats.
   *
   * TODO: Clean up this method once we know for sure what format viz-client passes.
   * Currently handles:
   * - IDataStore (already wrapped)
   * - Plain JavaScript object (HUTT JSON)
   * - JSON string (for edge cases)
   *
   * @param dataStore - Can be IDataStore, HUTT JSON object, or HUTT JSON string
   * @param debugName - Name for logging purposes
   * @returns IDataStore or null if parsing fails
   */
  private parseDataStore(dataStore: any, debugName: string): IDataStore | null {
    // Already an IDataStore
    if (dataStore && typeof dataStore.getValue === 'function') {
      return dataStore as IDataStore;
    }

    // Try to parse as HUTT JSON
    try {
      if (typeof dataStore === 'string') {
        const parsed = HuttParser.parseString(dataStore);
        return parsed;
      } else if (typeof dataStore === 'object' && dataStore !== null) {
        const parsed = HuttParser.parse(dataStore);
        return parsed;
      } else {
        console.error(`[MarkAnimationController] ${debugName} is not a valid type: ${typeof dataStore}`);
      }
    } catch (error) {
      console.error(`[MarkAnimationController] Error parsing ${debugName}:`, error);
    }

    return null;
  }

  /**
   * Clean up resources.
   */
  private cleanup(): void {
    if (this.currentAnimation) {
      // Cancel the animation if it's still playing
      const animationManager = AnimationManager.getInstance();
      animationManager.cancelAnimation(this.currentAnimation);
      this.currentAnimation = null;
    }
    this.currentInterpolatedFrame = null;
  }
}

/**
 * Global tick function for advancing all animations.
 *
 * This should be called once per frame (e.g., from requestAnimationFrame or a game loop).
 * It advances the AnimationManager which in turn updates all active animations.
 *
 * @param timestamp - Current timestamp in milliseconds (e.g., from performance.now())
 * @returns true if any controller has frames ready for rendering
 */
export function tick(timestamp: number): boolean {
  // Advance the animation manager
  const animationManager = AnimationManager.getInstance();
  animationManager.tick(timestamp);

  // Return true ONLY if any controller has frames ready NOW (not if animation is in progress)
  // This matches WASM behavior: frames ready → true, consumed → false until next tick
  return MarkAnimationController.hasAnyFramesReady();
}
